import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { SortConfig } from '@/utils/sorting'
import { queryStringToObject } from '@/utils/url'

type FilterConfig = Record<string, string | number | boolean | undefined>

type TableState<S extends SortConfig, F extends FilterConfig> = {
  activePage: number
  sortConfig: S | Record<string, never>
  filterConfig: F
}

const TABLE_URL_PARAMS = {
  activePage: 'page',
  sortKey: 'sortKey',
  sortOrder: 'sortOrder'
}

export const useTableState = <S extends SortConfig, F extends FilterConfig>(
  initialState: Partial<TableState<S, F>> = {}
) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const activePage =
    searchParams.get(TABLE_URL_PARAMS.activePage) ||
    String(initialState.activePage ?? '1')

  const sortKey =
    searchParams.get(TABLE_URL_PARAMS.sortKey) || initialState.sortConfig?.key

  const sortOrder =
    searchParams.get(TABLE_URL_PARAMS.sortOrder) ||
    initialState.sortConfig?.order

  const filterValues = useMemo(() => {
    const allParams = queryStringToObject(searchParams.toString())

    return Object.fromEntries(
      Object.entries(allParams).filter(
        ([key]) => !Object.values(TABLE_URL_PARAMS).includes(key)
      )
    )
  }, [searchParams])

  const tableState: TableState<S, F> = useMemo(
    () => ({
      activePage: Number(activePage),

      sortConfig:
        // `as S` is required for strict assignability to `S | Record<string, never>`, even though the lint rule
        // considers it redundant because the types overlap. Known false positive with generic constraints.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        sortKey && sortOrder ? ({ key: sortKey, order: sortOrder } as S) : {},

      filterConfig: filterValues as F
    }),
    [activePage, sortKey, sortOrder, filterValues]
  )

  const setTableState = useCallback(
    (newState: Partial<TableState<S, F>>) => {
      const { activePage: newPage, sortConfig, filterConfig } = newState

      const newParams = new URLSearchParams(searchParams)

      if (newPage) {
        newParams.set(TABLE_URL_PARAMS.activePage, String(newPage))
      }

      if (sortConfig?.key) {
        newParams.set(TABLE_URL_PARAMS.sortKey, sortConfig.key)
      }

      if (sortConfig?.order) {
        newParams.set(TABLE_URL_PARAMS.sortOrder, sortConfig.order)
      }

      if (filterConfig) {
        Object.entries(filterConfig).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            newParams.set(key, String(value))
          } else {
            newParams.delete(key)
          }
        })
      }

      setSearchParams(newParams, { replace: true })
    },

    [searchParams, setSearchParams]
  )

  return [tableState, setTableState] as const
}
